import { api } from '@/api';
import { appClient, Query } from '@/lib/appwrite/appClient';
import {
  APP_COLLECTIONS,
  AppTransaction,
  TransactionFilters,
  UNCATEGORIZED_FILTER,
} from '@polybank/shared';
import dayjs from 'dayjs';
import { logger } from '@/utils/logger';

export interface PaginatedTransactionsResponse {
  transactions: AppTransaction[];
  total: number;
  hasNextPage: boolean;
  nextCursor?: string;
}

export type SortBy =
  | 'date-desc'
  | 'date-asc'
  | 'amount-desc'
  | 'amount-asc'
  | 'description-asc'
  | 'description-desc';

export interface FetchTransactionsOptions extends Partial<TransactionFilters> {
  limit?: number;
  cursor?: string;
  /**
   * This will make the query to request one record only,
   * so we can get the count of a query without transferring
   * all the data to the client
   */
  onlyCount?: boolean;
  /**
   * Sort order for the transactions
   */
  sortBy?: SortBy;
}

export type RateLimitData = {
  combinedRemaining: number;
  resetTime: string | null;
};

// Per-account error summary
export type AccountErrorSummary = {
  message: string;
  errorType: string;
  isRateLimit: boolean;
  isAccessExpired: boolean;
};

// Per-account sync summary
export type AccountSyncSummary = {
  accountId: string;
  internalAccountId?: string;
  accountName?: string;
  balance?: number;
  transactionsFetched: number;
  transactionsSaved: number;
  error?: AccountErrorSummary;
};

// Complete sync response from backend
export type SyncCompleteResponse = {
  totalAccountsProcessed: number;
  successfulAccounts: number;
  failedAccounts: number;
  totalTransactionsSaved: number;
  accounts: AccountSyncSummary[];
  rateLimits: Record<string, RateLimitData>;
};

export type SyncTransactionsResult = {
  event: 'complete';
  data: SyncCompleteResponse;
};

export type SyncErrorEvent = {
  event: 'requisition-error';
  data: {
    error: string;
    errorType?: string;
    isAccessExpired?: boolean;
  };
};

/**
 * 🚀 EFFICIENT PAGINATION - This solves the mobile performance issues!
 *
 * Unlike Jazz's client-side TransactionList that loads all data,
 * this uses Appwrite's server-side pagination with database indexes
 */
async function fetchTransactionsPaginated(
  options: FetchTransactionsOptions
): Promise<PaginatedTransactionsResponse> {
  const {
    limit = 20,
    cursor,
    searchQuery,
    categories,
    accountFilter,
    dateRange,
    amountRange,
    transactionTypes,
    merchants,
    onlyCount,
    sortBy = 'date-desc',
  } = options;

  try {
    // Build sort query based on sortBy parameter
    let sortQuery: string;
    switch (sortBy) {
      case 'date-asc':
        sortQuery = Query.orderAsc('transaction_date');
        break;
      case 'amount-desc':
        sortQuery = Query.orderDesc('amount');
        break;
      case 'amount-asc':
        sortQuery = Query.orderAsc('amount');
        break;
      case 'description-asc':
        sortQuery = Query.orderAsc('description');
        break;
      case 'description-desc':
        sortQuery = Query.orderDesc('description');
        break;
      case 'date-desc':
      default:
        sortQuery = Query.orderDesc('transaction_date');
        break;
    }

    // Build query array starting with user filter and ordering
    const queries: string[] = [
      sortQuery,
      // if onlyCount is true, we will request one record only
      // so we can get the count of a query without transferring
      // all the data to the client
      Query.limit(onlyCount ? 1 : limit),
    ];

    // Add cursor for pagination
    if (cursor) {
      queries.push(Query.cursorAfter(cursor));
    }

    // Add filters based on provided options
    if (
      accountFilter &&
      Array.isArray(accountFilter) &&
      accountFilter.length > 0
    ) {
      logger.debug(
        `🎯 Filtering transactions by bank_account_id: ${accountFilter.join(', ')}`
      );
      queries.push(Query.equal('bank_account_id', accountFilter));
    } else {
      logger.debug(
        `📊 Loading transactions for all accounts (no account filter)`
      );
    }

    if (categories && categories.length > 0) {
      const hasUncategorized = categories.includes(UNCATEGORIZED_FILTER);
      const regularCategories = categories.filter(
        (c) => c !== UNCATEGORIZED_FILTER
      );

      logger.debug(
        `🏷️ Filtering transactions by categories: ${categories.join(', ')} (uncategorized: ${hasUncategorized})`
      );

      if (hasUncategorized && regularCategories.length > 0) {
        // Filter for both uncategorized AND specific categories
        queries.push(
          Query.or([
            Query.isNull('category_id'),
            Query.equal('category_id', regularCategories),
          ])
        );
      } else if (hasUncategorized) {
        // Filter for only uncategorized transactions
        queries.push(Query.isNull('category_id'));
      } else {
        // Filter for only specific categories
        queries.push(Query.equal('category_id', regularCategories));
      }
    }

    if (transactionTypes && transactionTypes.length > 0) {
      queries.push(Query.equal('transaction_type', transactionTypes));
    }

    if (merchants && merchants.length > 0) {
      logger.debug(
        `🏪 Filtering transactions by merchants: ${merchants.join(', ')}`
      );
      queries.push(Query.equal('merchant_name', merchants));
    }

    // Date range filter using our indexed date field
    if (dateRange) {
      if (dateRange.from) {
        queries.push(
          Query.greaterThanEqual(
            'transaction_date',
            dayjs(dateRange.from.toISOString())
              .startOf('day')
              .format('YYYY-MM-DDTHH:mm:ss')
          )
        );
      }
      if (dateRange.to) {
        queries.push(
          Query.lessThanEqual(
            'transaction_date',
            dayjs(dateRange.to.toISOString())
              .endOf('day')
              .format('YYYY-MM-DDTHH:mm:ss')
          )
        );
      }
    }

    // Amount range filter using our indexed amount field
    if (amountRange) {
      logger.debug(
        `💰 Filtering transactions by amount range: ${amountRange.min} - ${amountRange.max}`
      );
      // Since amounts can be negative (debits) or positive (credits),
      // we need to filter for both ranges: [-max, -min] OR [min, max]
      queries.push(
        Query.or([
          Query.and([
            Query.greaterThanEqual('amount', -amountRange.max),
            Query.lessThanEqual('amount', -amountRange.min),
          ]),
          Query.and([
            Query.greaterThanEqual('amount', amountRange.min),
            Query.lessThanEqual('amount', amountRange.max),
          ]),
        ])
      );
    }

    // Full-text search using our fulltext index
    if (searchQuery && searchQuery.trim()) {
      queries.push(Query.contains('description_indexed', searchQuery.trim()));
    }

    //sort by date
    // Execute the optimized database query
    logger.debug(`Executing query with ${queries.length} filters:`, queries);
    const response = await appClient.tables.typed.listRows(
      APP_COLLECTIONS.TRANSACTIONS,
      queries
    );
    logger.debug(
      `📊 Query returned ${response.rows.length} transactions out of ${response.total} total`
    );

    // Convert Appwrite documents to UI Transaction format with resolved relations
    const transactions = response.rows.map((doc) => {
      const appwriteTransaction = doc;

      return appwriteTransaction;
    });

    // Determine if there are more pages
    const hasNextPage = response.rows.length === limit;
    const nextCursor = hasNextPage
      ? response.rows[response.rows.length - 1].$id
      : undefined;

    logger.debug(
      `📄 Appwrite pagination: ${transactions.length} transactions loaded, hasNext: ${hasNextPage}`
    );

    return {
      transactions,
      total: response.total,
      hasNextPage,
      nextCursor,
    };
  } catch (error) {
    logger.error('Failed to fetch transactions:', error);
    return {
      transactions: [],
      total: 0,
      hasNextPage: false,
    };
  }
}

/**
 * Delete a transaction
 */
async function deleteTransaction(transactionId: string): Promise<void> {
  try {
    await appClient.tables.typed.deleteRow(
      APP_COLLECTIONS.TRANSACTIONS,
      transactionId
    );
  } catch (error) {
    logger.error('Failed to delete transaction:', error);
    throw error;
  }
}

type SyncReturnType = Awaited<
  ReturnType<(typeof api)['v1']['transactions']['sync']['post']>
>['data'] extends AsyncGenerator<infer U, void, unknown> | null
  ? U
  : never;

async function syncTransactions(
  requisitionId: string,
  bankAccountId?: string,
  options?: {
    abortController?: AbortController;
    callbacks: {
      onRequisitionRetrieved?: () => void;
      onAccountProcessed?: () => void;
      onTransactionsRetrieved?: () => void;
      onComplete?: (data: SyncCompleteResponse) => void;
      onRateLimitUpdate?: (rateLimits: Record<string, RateLimitData>) => void;
      onError?: (error: SyncErrorEvent['data']) => void;
      finally?: () => void;
    };
  }
): Promise<void> {
  logger.debug('Starting transaction sync...', {
    requisitionId,
    bankAccountId,
  });

  const { error, data } = await api.v1.transactions.sync.post(
    {
      requisitionId,
      accountId: bankAccountId,
    },
    { fetch: { signal: options?.abortController?.signal } }
  );

  logger.debug('API response received:', {
    hasError: !!error,
    hasData: !!data,
  });

  if (error) {
    logger.error('API error:', error);
    throw error;
  }

  if (!data) {
    logger.error('No data received from API');
    throw new Error('No data stream received from sync endpoint');
  }

  logger.debug('Starting to consume async generator...');
  try {
    for await (const chunk of data) {
      logger.debug('SSE chunk received:', chunk);
      const eventData = chunk as SyncReturnType;

      if (eventData.event === 'requisition-retrieved') {
        options?.callbacks.onRequisitionRetrieved?.();
      } else if (eventData.event === 'account-processed') {
        options?.callbacks.onAccountProcessed?.();
      } else if (eventData.event === 'transactions-retrieved') {
        options?.callbacks.onTransactionsRetrieved?.();
      } else if (eventData.event === 'complete') {
        const completeData = eventData.data as SyncCompleteResponse;
        options?.callbacks.onComplete?.(completeData);

        // Call rate limit callback if rate limits are present
        if (completeData.rateLimits) {
          options?.callbacks.onRateLimitUpdate?.(completeData.rateLimits);
        }
      } else if (eventData.event === 'requisition-error') {
        // Handle requisition error (critical error that prevents sync)
        logger.error('Sync error received:', eventData);
        const errorData = eventData.data as SyncErrorEvent['data'];
        options?.callbacks.onError?.(errorData);
      }
    }
  } catch (err) {
    logger.error('Error consuming async generator:', err);
    options?.callbacks.onError?.({
      error: err instanceof Error ? err.message : String(err),
      errorType: 'generic-error',
    });
  }

  logger.debug('Async generator consumed completely');
  options?.callbacks.finally?.();
}
async function getOldestTransactionDate(): Promise<string | null> {
  const result = await appClient.tables.typed.listRows(
    APP_COLLECTIONS.TRANSACTIONS,
    [Query.orderAsc('transaction_date'), Query.limit(1)]
  );

  if (result.rows.length > 0) {
    return result.rows[0].transaction_date;
  }

  return null;
}

async function updateTransactionCategory(
  transactionId: string,
  categoryId: string | undefined
) {
  return appClient.tables.typed.updateRow(
    APP_COLLECTIONS.TRANSACTIONS,
    transactionId,
    {
      category_id: categoryId,
      applied_rules: [],
    }
  );
}

async function updateRuleRaw(
  ruleId: string,
  data: { raw: string; humanReadable: string }
) {
  return appClient.tables.typed.updateRow(APP_COLLECTIONS.RULES, ruleId, data);
}
async function updateTransactionMerchant(
  transactionId: string,
  merchantId: string
) {
  return appClient.tables.typed.updateRow(
    APP_COLLECTIONS.TRANSACTIONS,
    transactionId,
    {
      merchant_name: merchantId,
    }
  );
}
async function getTransaction(
  transactionId: string
): Promise<AppTransaction | null> {
  const result = await appClient.tables.typed.getRow(
    APP_COLLECTIONS.TRANSACTIONS,
    transactionId
  );
  return result;
}
const transactionsService = {
  getTransaction,
  fetchTransactionsPaginated,
  deleteTransaction,
  syncTransactions,
  getOldestTransactionDate,
  updateTransactionCategory,
  updateRuleRaw,
  updateTransactionMerchant,
};

export default transactionsService;
